import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SeoMetadata from "@/components/common/SeoMetadata";
import { useEffect } from "react";
import { saveSeoMetadata } from "@/utils/seoUtils";
import { Link } from "react-router-dom";
import { Car, ArrowRight, Wrench, Shield, Navigation, Gauge, Clock, CheckCircle, FileText, BarChart, AlertCircle, Fuel, DollarSign } from "lucide-react";
import { LainaCta } from "@/components/common/LainaCta";
import ImageOverlay from "@/components/common/ImageOverlay";
import Image from "@/components/ui/image";
import { useIsMobile } from "@/hooks/use-mobile";
import AffiliateAds from "@/components/common/AffiliateAds";

const seoKeywords = [
  "autolaina", "autorahoitus", "auton osamaksu", 
  "auto leasing", "edullinen autolaina", 
  "auton osto lainalla", "autolainan korko",
  "autolaina ilman käsirahaa", "autolainan kilpailutus",
  "käytetyn auton rahoitus"
];

const AutolainaPage = () => {
  const seoTitle = "Autolaina | Kilpailuta edullinen rahoitus autoon";
  const seoDescription = "Löydä edullisin autolaina ja kilpailuta autorahoitus Lainasampo.fi kautta. Vertaile lainatarjouksia ja säästä autorahoituksessa.";
  const pageUrl = "https://lainasampo.fi/lainat/autolaina";
  const isMobile = useIsMobile();
  
  useEffect(() => {
    saveSeoMetadata("/lainat/autolaina", {
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords,
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SeoMetadata
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={pageUrl}
      />
      
      <div className="relative h-[60vh] md:h-[70vh] min-h-[300px] md:min-h-[400px] max-h-[500px] md:max-h-[600px]">
        <Image 
          src="/lovable-uploads/74a42011-2e94-4e1e-8924-91af3a378d8d.png"
          alt="Nainen avoimessa autossa nauttimassa ajelusta" 
          className="absolute inset-0 w-full h-full object-cover"
          fallbackSrc="/placeholder.svg"
          priority={true}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent md:from-black/70 md:via-black/50 flex items-center">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-2xl text-white space-y-4 md:space-y-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight drop-shadow-lg">
                Hanki <span className="text-blue-400">paras autolaina</span> helposti
              </h1>
              <p className="text-base sm:text-lg md:text-xl drop-shadow-md">
                Kilpailuta autolaina ilmaiseksi ja löydä paras tarjous. Rahoita myös varusteet ja vakuutukset samalla lainalla.
              </p>
              <div className="pt-2 md:pt-4 flex flex-col sm:flex-row gap-3 md:gap-4">
                <LainaCta 
                  text={isMobile ? "Hae lainaa" : "Hae autolainaa nyt"} 
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold shadow-lg transform hover:scale-105 transition duration-300"
                  variant="primary"
                  size={isMobile ? "md" : "lg"}
                />
                {!isMobile && (
                  <Link to="/lainatuotteet" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 border border-white/30 backdrop-blur-sm text-white font-medium transition-all duration-300">
                    Vertaile lainoja <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 -mt-12 md:-mt-24 mb-8 md:mb-16 relative z-10">
          {[
            { text: isMobile ? "Päätös 24h" : "Päätös 24h sisällä", icon: <Clock className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "Korot alk. 4,5%" : "Edulliset korot alkaen 4,5%", icon: <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "Ilman vakuuksia" : "Ilman vakuuksia jopa 50 000€", icon: <Shield className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "1-8v maksuaika" : "Maksuaika 1-8 vuotta", icon: <Gauge className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> }
          ].map((item, i) => (
            <div key={i} className="bg-white px-3 py-2 md:px-6 md:py-3 rounded-full shadow-md flex items-center gap-1.5 md:gap-2 text-sm md:text-base">
              {item.icon}
              <span className="font-medium">{item.text}</span>
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-16">
          <div className="lg:col-span-2 space-y-8 md:space-y-12">
            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-8 relative">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                  Miksi valita autolaina?
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  {
                    icon: <Car className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Joustava rahoitus",
                    description: "Räätälöi laina-aika ja kuukausierä omaan budjettiisi sopivaksi."
                  },
                  {
                    icon: <Shield className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Kattava ratkaisu",
                    description: "Sisällytä myös varusteet ja vakuutukset samaan rahoitukseen."
                  },
                  {
                    icon: <Gauge className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Nopea käsittely",
                    description: "Saat lainapäätöksen nopeasti ja voit heti lähteä toteuttamaan suunnitelmaasi."
                  }
                ].map((feature, index) => (
                  <div key={index} className="relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl transform group-hover:scale-105 transition-transform duration-300"></div>
                    <Card className="h-full bg-transparent border-0 relative z-10 bg-white/95 backdrop-blur-sm group-hover:bg-white/100 transition-all">
                      <CardContent className="pt-4 md:pt-6">
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r from-blue-600 to-blue-800 flex items-center justify-center mb-3 md:mb-4 group-hover:shadow-lg transition-all">
                          {feature.icon}
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-blue-800 mb-1 md:mb-2">{feature.title}</h3>
                        <p className="text-sm md:text-base text-gray-700">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </section>
            
            <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <div className="relative rounded-xl overflow-hidden mb-4 md:mb-6 group transform hover:scale-[1.01] transition-all duration-300">
                  <Image
                    src="/lovable-uploads/3753f2a3-96af-4a62-8470-f8461f6a84a3.png"
                    alt="Musta Audi autotallien edustalla nupukivitiellä"
                    className="w-full h-[200px] md:h-[300px] object-cover"
                    fallbackSrc="/placeholder.svg"
                    priority={true}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <h3 className="text-white text-lg md:text-xl font-bold">Varusteet ja vakuutukset</h3>
                  </div>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 md:mb-4">Varusteet ja vakuutukset samaan pakettiin</h2>
                <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">
                  Autolainamme kattaa myös tarvittavat varusteet ja vakuutukset. Näin voit keskittyä ajamisen iloon ja jättää rahoitusasiat meidän hoidettavaksemme.
                </p>
                <div className="space-y-2 md:space-y-3">
                  <h3 className="font-bold text-blue-700 text-sm md:text-base">Sisällytä lainaan:</h3>
                  <ul className="space-y-1 md:space-y-2">
                    {[
                      "Vakuutukset ja rekisteröintimaksut",
                      "Lisävarusteet ja muutostyöt",
                      "Huoltopaketit ja talvirenkaat",
                      "Polttoainekortit ja tiepalvelut"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm md:text-base text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <div className="relative rounded-xl overflow-hidden mb-4 md:mb-6 group transform hover:scale-[1.01] transition-all duration-300">
                  <Image
                    src="/lovable-uploads/231573f3-1c94-4fd7-9faf-43d3982d3f33.png"
                    alt="Punainen Mustang urheiluauto"
                    className="w-full h-[200px] md:h-[300px] object-cover"
                    fallbackSrc="/placeholder.svg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <h3 className="text-white text-lg md:text-xl font-bold">Kilpailuta lainatarjoukset</h3>
                  </div>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 md:mb-4">Kilpailuta autolainasi helposti</h2>
                <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">
                  Lainasampo.fi välittää lainahakemuksesi noin 30 pankille ja rahoitusyhtiölle. Vertaile tarjouksia rauhassa ja valitse itsellesi sopivin vaihtoehto.
                </p>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 md:p-5 rounded-xl border border-blue-200">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-sm md:text-base">Lainan tiedot</h3>
                  <div className="space-y-2 md:space-y-3">
                    {[
                      { label: "Lainasumma:", value: "5 000€ - 70 000€" },
                      { label: "Laina-aika:", value: "1-8 vuotta" },
                      { label: "Korko alkaen:", value: "4,5%" },
                      { label: "Vakuus:", value: "Vakuudeton tai auto vakuutena" }
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between text-sm md:text-base">
                        <span className="text-gray-700 font-medium">{item.label}</span>
                        <span className="font-bold text-blue-800">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-8 relative">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                  Mitä huomioida autolainassa?
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5">
                <div className="relative overflow-hidden group">
                  <div className="absolute inset-0 bg-blue-50 rounded-xl transform translate-y-2 translate-x-2 group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300"></div>
                  <div className="p-3 md:p-5 bg-white border border-blue-100 rounded-xl relative group-hover:shadow-md transition-all">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 flex items-center justify-center mb-2 md:mb-3">
                      <Car className="h-4 w-4 md:h-5 md:w-5 text-white" />
                    </div>
                    <h4 className="font-bold text-blue-800 mb-1 md:mb-2 text-sm md:text-base">Kokonaiskulut</h4>
                    <p className="text-xs md:text-sm text-gray-700">Huomioi koron lisäksi myös avausmaksut, käsittelykulut ja muut mahdolliset lisämaksut.</p>
                  </div>
                </div>
                
                <div className="relative overflow-hidden group">
                  <div className="absolute inset-0 bg-green-50 rounded-xl transform translate-y-2 translate-x-2 group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300"></div>
                  <div className="p-3 md:p-5 bg-white border border-green-100 rounded-xl relative group-hover:shadow-md transition-all">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-green-500 to-green-700 flex items-center justify-center mb-2 md:mb-3">
                      <Clock className="h-4 w-4 md:h-5 md:w-5 text-white" />
                    </div>
                    <h4 className="font-bold text-green-800 mb-1 md:mb-2 text-sm md:text-base">Laina-aika</h4>
                    <p className="text-xs md:text-sm text-gray-700">Pidempi laina-aika pienentää kuukausierää, mutta nostaa kokonaiskustannuksia.</p>
                  </div>
                </div>
                
                <div className="relative overflow-hidden group">
                  <div className="absolute inset-0 bg-purple-50 rounded-xl transform translate-y-2 translate-x-2 group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300"></div>
                  <div className="p-3 md:p-5 bg-white border border-purple-100 rounded-xl relative group-hover:shadow-md transition-all">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-purple-500 to-purple-700 flex items-center justify-center mb-2 md:mb-3">
                      <Shield className="h-4 w-4 md:h-5 md:w-5 text-white" />
                    </div>
                    <h4 className="font-bold text-purple-800 mb-1 md:mb-2 text-sm md:text-base">Vakuudet</h4>
                    <p className="text-xs md:text-sm text-gray-700">Selvitä, toimiiko auto vakuutena vai tarvitaanko muita vakuuksia.</p>
                  </div>
                </div>
                
                <div className="relative overflow-hidden group">
                  <div className="absolute inset-0 bg-orange-50 rounded-xl transform translate-y-2 translate-x-2 group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300"></div>
                  <div className="p-3 md:p-5 bg-white border border-orange-100 rounded-xl relative group-hover:shadow-md transition-all">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-700 flex items-center justify-center mb-2 md:mb-3">
                      <Wrench className="h-4 w-4 md:h-5 md:w-5 text-white" />
                    </div>
                    <h4 className="font-bold text-orange-800 mb-1 md:mb-2 text-sm md:text-base">Joustavuus</h4>
                    <p className="text-xs md:text-sm text-gray-700">Tarkista, onko lainassa mahdollisuus maksuvapaisiin kuukausiin tai ennenaikaiseen takaisinmaksuun.</p>
                  </div>
                </div>
              </div>
            </section>
            
            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6 relative">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                  Hyödyllisiä artikkeleita
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-5 mt-4 md:mt-8">
                {[
                  {
                    title: "Autolainan hakijan opas",
                    url: "/artikkelit/autolaina-ohjeet",
                    description: "Mitä tulee tietää ennen autolainan hakemista"
                  },
                  {
                    title: "Autolaina vai leasing?",
                    url: "/artikkelit/autolaina-vs-leasing",
                    description: "Kumpi sopii sinun tilanteeseesi paremmin"
                  },
                  {
                    title: "Autokaupan rahoitus",
                    url: "/artikkelit/autokaupan-rahoitus",
                    description: "Autokaupan rahoituksen sudenkuopat"
                  }
                ].map((article, i) => (
                  <Link to={article.url} key={i} className="group">
                    <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 h-full transform group-hover:-translate-y-1 group-hover:shadow-lg transition-all duration-300">
                      <div className="p-5">
                        <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">{article.title}</h3>
                        <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">{article.description}</p>
                        <div className="flex items-center text-blue-600 font-medium">
                          Lue lisää <ArrowRight className="ml-2 h-4 w-4 group-hover:ml-3 transition-all" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>
          
          <div className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <div className="relative overflow-hidden rounded-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800"></div>
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#pattern)" />
                    <defs>
                      <pattern id="pattern" patternUnits="userSpaceOnUse" width="10" height="10">
                        <circle cx="5" cy="5" r="1" fill="white" />
                      </pattern>
                    </defs>
                  </svg>
                </div>
                <div className="relative p-6 text-white">
                  <h2 className="text-2xl font-bold mb-4">Kilpailuta autolainasi nyt!</h2>
                  <p className="mb-6">
                    Auton rahoituksessa voit säästää jopa 30% lainasi kokonaiskustannuksissa!
                  </p>
                  <LainaCta 
                    variant="primary" 
                    size="lg" 
                    text="Hae autolainaa" 
                    className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold shadow-md"
                  />
                </div>
              </div>
              
              <Card className="border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
                  <CardTitle className="text-lg text-white">Autolaina lyhyesti</CardTitle>
                </div>
                <CardContent className="pt-5">
                  <div className="space-y-3">
                    {[
                      { label: "Laina-aika:", value: "1-8 vuotta" },
                      { label: "Lainasumma:", value: "5 000€ - 70 000€" },
                      { label: "Korko alkaen:", value: "4,5%" },
                      { label: "Vakuus:", value: "Vakuudeton tai auto vakuutena" },
                      { label: "Käsittelyaika:", value: "1-3 arkipäivää" }
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-bold text-blue-800">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-bold">Säästövinkki:</span> Kilpailuttamalla voit säästää jopa 30% lainasi kokonaiskustannuksissa!
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="font-bold text-lg mb-4 text-gray-800">Katso myös nämä rahoitusvaihtoehdot</h3>
                <div className="space-y-3">
                  {[
                    { name: "Moottoripyörälaina", url: "/lainat/moottoripyoralaina" },
                    { name: "Venelaina", url: "/lainat/venelaina" },
                    { name: "Caravanrahoitus", url: "/lainat/caravanrahoitus" },
                    { name: "Kulutusluotto", url: "/lainat/kulutusluotto" }
                  ].map((option, i) => (
                    <Link
                      key={i}
                      to={option.url}
                      className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-100 text-gray-800 hover:text-blue-700 font-medium transition-colors group"
                    >
                      <span>{option.name}</span>
                      <ArrowRight className="ml-auto h-4 w-4 text-gray-400 group-hover:text-blue-500 transform group-hover:translate-x-1 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <section className="mb-8 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
              Erityyliset autot - löydä unelmiesi ajoneuvo
            </span>
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-12">
            {[
              {
                url: "/lovable-uploads/beecd55f-24fd-4a17-bedd-1b1021faa9d5.png",
                title: "Klassikot"
              },
              {
                url: "/lovable-uploads/ed2c988e-b47e-41cd-a09e-89c67b462cf2.png",
                title: "Katumaasturit"
              },
              {
                url: "/lovable-uploads/7ee0fd35-881c-4057-a591-fc00f4c62062.png",
                title: "Urheiluautot"
              }
            ].map((item, i) => (
              <div key={i} className="relative overflow-hidden rounded-xl aspect-video group">
                <Image 
                  src={item.url} 
                  alt={item.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  fallbackSrc="/placeholder.svg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end">
                  <div className="p-4 md:p-6">
                    <h3 className="text-white text-lg md:text-xl font-bold">{item.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
            {[
              "/lovable-uploads/79c54a4a-f3bf-4fe7-a4d5-43408ec1285e.png",
              "/lovable-uploads/d5800765-c598-426f-910b-df510d45144e.png",
              "/lovable-uploads/b823ae92-c14e-4f0e-995f-ea7e01f4998b.png",
              "/lovable-uploads/4f63513d-7c32-4d9a-b8bf-868015ed82e2.png"
            ].map((url, i) => (
              <div key={i} className="relative overflow-hidden rounded-lg aspect-square group">
                <Image 
                  src={url} 
                  alt="Auto"
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  fallbackSrc="/placeholder.svg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mt-6 md:mt-12">
            {[
              "/lovable-uploads/6ad56e65-7333-4aef-9ad9-0966914e84fe.png",
              "/lovable-uploads/62748077-9827-4a84-9e06-05513e6349bf.png"
            ].map((url, i) => (
              <div key={i} className="relative overflow-hidden rounded-xl aspect-video group">
                <Image 
                  src={url} 
                  alt="Auton omistajan elämäntyyli"
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  fallbackSrc="/placeholder.svg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            ))}
          </div>
        </section>
        
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl md:rounded-2xl overflow-hidden mb-8 md:mb-16 relative">
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path fill="white" d="M47.1,-57.8C58.5,-45.7,63.7,-28.7,66.8,-11.1C70,6.5,71.1,24.5,63.5,38.6C55.9,52.7,39.6,62.8,22.2,67.6C4.9,72.3,-13.5,71.6,-29.6,64.7C-45.8,57.8,-59.6,44.7,-68.5,28C-77.4,11.3,-81.3,-9,-74.5,-25.3C-67.8,-41.6,-50.5,-54,-33.7,-65.1C-16.9,-76.2,-0.6,-86,14.8,-83.5C30.3,-81,35.7,-69.9,47.1,-57.8Z" />
            </svg>
          </div>
          <div className="relative p-5 md:p-8 text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 md:mb-4">Aloita autolainan hakeminen tänään!</h2>
            <p className="text-sm md:text-lg mb-4 md:mb-6 max-w-2xl">
              Kilpailuta lainatarjoukset ilmaiseksi ja ilman sitoumuksia. Saat lainapäätöksen nopeasti ja voit valita juuri sinulle sopivan rahoitusvaihtoehdon.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <LainaCta 
                text={isMobile ? "Hae rahoitusta" : "Hae rahoitusta autoon"} 
                className="bg-white text-blue-700 hover:bg-blue-50 font-bold"
                variant="primary"
                size={isMobile ? "md" : "lg"}
              />
              {!isMobile && (
                <Link to="/lainatuotteet" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-blue-700 hover:bg-blue-800 border border-blue-500 text-white font-medium transition-all duration-300">
                  Vertaile lainatuotteita <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AutolainaPage;
